/*
 * Maison Ferré native cart (replaces Kaching Cart).
 * Liquid (snippets/mf-cart-eligibility.liquid) owns every rule and prints them on [data-mf-cart-state].
 * This file (1) keeps the gift lines equal to their targets: Gift Bundle qty = pans, each gift tier variant qty 1 when
 * the paid total reaches its threshold (× Shopify.currency.rate), else 0; (2) paints the gift ladder bar and euro
 * amounts in the visitor's currency; (3) handles −/+ steppers, the inline variant select, the shipping protection
 * switch and "Complétez votre cuisine"; (4) exposes window.MFCart.refresh() for the buy box.
 *
 * Hooks:     document "cart:change" (Impact, detail.cart may carry rendered sections), document "submit" (upsell
 *            forms), document "change"/"click" (protection, variant select, upsell select, steppers)
 * Dispatches: "cart:prepare-bundled-sections" (collect section ids, as Impact does),
 *            "cart:change" (detail.baseEvent "variant:add"), "variant:add" (opens the drawer)
 */
(() => {
  if (window.MFCart) return;

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const settings = () => (window.themeVariables && window.themeVariables.settings) || {};
  const onCartPage = () => settings().pageType === 'cart';
  const rate = () => Number(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
  const fire = (name, detail) => document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));

  // Same output as mf-buybox.js / the theme's formatMoney, tags stripped, € glued with a no-break space.
  const FORMATS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_space_separator: [2, ' ', '.'],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_apostrophe_separator: [0, "'", '.']
  };
  function money(cents) {
    const s = settings();
    const f = (s.currencyCodeEnabled ? s.moneyWithCurrencyFormat : s.moneyFormat) || '{{amount}}';
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      const [precision, thousands, decimal] = FORMATS[key] || FORMATS.amount;
      const [int, frac] = (Math.round(cents) / 100).toFixed(precision).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    }).replace(/<[^>]*>/g, '').replace(/ €/g, ' €');
  }
  const esc = (str) => String(str).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  // Theme setting mf_gift_tiers "399.95:variant:label:128.95[:handle]|…" (euros, ascending) -> shop cents, same order.
  // A tier without a variant id is dropped (never promised); the Liquid dots skip the same rows.
  // index = position in the setting, which is the tier number of the locale label mf.gifts.tier_label_<index + 1>.
  const parseTiers = (str) => String(str || '').split('|').map((row, index) => {
    const [at, variant, label = '', value] = row.split(':');
    return { index, at: Math.round(parseFloat(at) * 100), variant: parseInt(variant, 10) || 0, label: label.trim(), value: Math.round(parseFloat(value) * 100) || 0 };
  }).filter((tier) => tier.variant > 0);
  const atLocal = (tier) => Math.round(tier.at * rate());
  const reached = (tier, base) => tier.at > 0 && base >= atLocal(tier);

  /* ---------- painting (gift ladder bar, euro amounts) ---------- */

  function paintTiers(bar) {
    const tiers = parseTiers(bar.dataset.tiers);
    const base = parseInt(bar.dataset.base, 10) || 0;
    let t = {};
    try { t = JSON.parse(bar.dataset.i18n || '{}'); } catch (error) { /* keep defaults */ }
    const valid = tiers.filter((tier) => tier.at > 0);
    if (!valid.length) return;

    const next = valid.find((tier) => !reached(tier, base));
    const shown = valid.filter((tier) => reached(tier, base) || tier === next).reduce((sum, tier) => sum + tier.value, 0);
    const msg = bar.querySelector('[data-mf-tiers-msg]');
    const sub = bar.querySelector('[data-mf-tiers-sub]');
    if (msg) {
      msg.innerHTML = next
        ? String(t.next || '').replace('@@amount@@', esc(money(atLocal(next) - base))).replace('@@gift@@', esc((t.labels || [])[next.index] || next.label))
        : esc(t.done || '');
    }
    if (sub) sub.textContent = shown > 0 ? String(t.value || '').replace('@@amount@@', money(Math.round(shown * rate()))) : '';

    // Each tier owns an equal segment of the track; the fill moves linearly between two thresholds.
    let fill = 100;
    for (let i = 0; i < tiers.length; i += 1) {
      if (!(tiers[i].at > 0) || reached(tiers[i], base)) continue;
      const prev = i > 0 && tiers[i - 1].at > 0 ? atLocal(tiers[i - 1]) : 0;
      fill = ((i + Math.max(0, base - prev) / Math.max(1, atLocal(tiers[i]) - prev)) / tiers.length) * 100;
      break;
    }
    const fillEl = bar.querySelector('[data-mf-tiers-fill]');
    if (fillEl) fillEl.style.width = `${fill.toFixed(1)}%`;
    const track = bar.querySelector('[role="progressbar"]');
    if (track) track.setAttribute('aria-valuenow', String(Math.round(fill)));
    bar.querySelectorAll('[data-mf-tier]').forEach((dot, i) => dot.classList.toggle('is-reached', !!tiers[i] && reached(tiers[i], base)));
  }

  // The gifts <details> is collapsed in the Liquid; Impact's innerHTML swaps would close it after every change.
  let giftsOpen = false;
  document.addEventListener('toggle', (event) => {
    if (event.target.matches && event.target.matches('.mf-giftbox')) giftsOpen = event.target.open;
  }, true);

  function paint(scope) {
    scope.querySelectorAll('.mf-giftbox').forEach((box) => { box.open = giftsOpen; });
    scope.querySelectorAll('[data-mf-tiers]').forEach(paintTiers);
    if (rate() === 1) return; // Liquid already printed euros
    scope.querySelectorAll('[data-mf-eur]').forEach((el) => {
      el.textContent = money(Math.round((parseInt(el.dataset.mfEur, 10) || 0) * rate()));
    });
  }

  // The drawer is re-rendered by Impact (innerHTML swaps): repaint in the same frame, only when our nodes arrive
  // (the countdown's per-second text changes are ignored).
  const PAINTED = '[data-mf-tiers],[data-mf-eur],.mf-giftbox';
  const watchDrawer = () => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || !window.MutationObserver) return;
    new MutationObserver((records) => {
      const ours = records.some((r) => Array.prototype.some.call(r.addedNodes, (n) => n.nodeType === 1 && (n.matches(PAINTED) || n.querySelector(PAINTED))));
      if (ours) paint(drawer);
    }).observe(drawer, { childList: true, subtree: true });
  };

  /* ---------- cart requests ---------- */

  const bundledSections = () => {
    const sections = [];
    fire('cart:prepare-bundled-sections', { sections });
    return sections;
  };

  const postJson = async (path, body) => {
    const response = await fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw Object.assign(new Error(`${path} ${response.status}`), { status: response.status });
    return response.json();
  };

  const fetchCart = async () => (await fetch(`${root()}cart.js`)).json();
  const linesOf = (cart) => cart.items.map((item) => ({ v: item.variant_id, q: item.quantity, k: item.key }));

  // Set the lines of `variant` to `required`: merge extra lines into the first, add one when there is none.
  const pushGift = async (variant, required, keys, sections) => {
    if (keys.length === 0) {
      const added = await postJson('cart/add.js', { items: [{ id: variant, quantity: required }], sections });
      const cart = await fetchCart();
      cart.sections = added.sections;
      return cart;
    }
    for (const extraKey of keys.slice(1)) await postJson('cart/change.js', { id: extraKey, quantity: 0, sections });
    return postJson('cart/change.js', { id: keys[0], quantity: required, sections });
  };

  // Impact (theme.js CartDrawer._onCartChanged) swaps .cart-drawer__inner 1250 ms after a cart:change that is not
  // "variant:add". A swap queued by the customer's quantity change, before our gift sync, lands after our render and
  // puts back the pre-sync snapshot (gift qty and header count). Check once after that window; re-render only if
  // the drawer's count disagrees with cart.js. refresh() fires a "variant:add" change (0 ms swap), so no new check.
  const verifyAfterThemeSwap = () => setTimeout(async () => {
    try {
      const shown = document.querySelector('#cart-drawer .cart-drawer__inner [data-mf-item-count]');
      if (!shown) return;
      const cart = await fetchCart();
      if (parseInt(shown.dataset.mfItemCount, 10) !== cart.item_count) await refresh({ open: false });
    } catch (error) {
      console.warn('[MFCart] drawer re-check failed', error);
    }
  }, 1400);

  /* ---------- gift sync ---------- */

  // A tier gift that is charged (p > 0: its BXGY did not apply, e.g. market rounding, a code that does not combine,
  // or the short window after the cart drops below the threshold) is removed and, when its tier is reached, not
  // re-added during this page life: a charged "gift" must never reach checkout.
  const charged = new Set();

  // targets: Map(variant -> wanted qty). lines: [{v, q, k, p}] gift lines currently in the cart (p = final_line_price).
  const parseState = (scope) => {
    const el = scope && scope.querySelector('[data-mf-cart-state]');
    if (!el) return null;
    const d = el.dataset;
    let lines = [];
    try { lines = JSON.parse(d.giftLines || '[]'); } catch (error) { return null; }
    const base = parseInt(d.tierBase, 10) || 0;
    const targets = new Map();
    const bundle = parseInt(d.giftVariant, 10);
    if (bundle) targets.set(bundle, parseInt(d.requiredGifts, 10) || 0);
    parseTiers(d.tiers).forEach((tier) => {
      const isReached = reached(tier, base);
      if (isReached && lines.some((l) => l.v === tier.variant && l.p > 0)) charged.add(tier.variant);
      targets.set(tier.variant, isReached && !charged.has(tier.variant) ? 1 : 0);
    });
    // Shipping protection is the customer's choice, except that it goes once no paid line is left.
    const protection = parseInt(d.protectionVariant, 10);
    if (protection && base === 0) targets.set(protection, 0);
    return { targets, lines };
  };

  const qtyOf = (lines, variant) => lines.filter((l) => l.v === variant).reduce((sum, l) => sum + l.q, 0);
  const mismatches = (targets, lines) => [...targets].filter(([variant, want]) => qtyOf(lines, variant) !== want);

  // Fresh state: the server-rendered DOM (page load), the sections bundled with the cart:change event,
  // else a re-render of the drawer section (event without sections).
  const readState = async (html) => {
    if (html === DOM) return parseState(document);
    if (html) return parseState(new DOMParser().parseFromString(html, 'text/html'));
    if (!onCartPage()) {
      const ids = bundledSections();
      if (ids.length) {
        const rendered = await (await fetch(`${root()}?sections=${ids.join(',')}`)).json();
        const found = Object.values(rendered).find((h) => typeof h === 'string' && h.includes('data-mf-cart-state'));
        if (found) return parseState(new DOMParser().parseFromString(found, 'text/html'));
      }
    }
    return parseState(document);
  };

  const DOM = Symbol('dom');
  let latestHtml = null;
  let timer = null;
  let busy = false;
  // variant -> [want, n]: n = times the sync had to push the same target again (Shopify capped or undid it).
  // Stop after 2 per variant per page (no loops, also when two variants alternate).
  const repushed = new Map();

  const schedule = (html) => {
    latestHtml = html || null;
    clearTimeout(timer);
    timer = setTimeout(reconcile, 300);
  };

  async function reconcile() {
    if (busy) {
      timer = setTimeout(reconcile, 300);
      return;
    }
    busy = true;
    const html = latestHtml;
    latestHtml = null;

    try {
      const state = await readState(html);
      if (!state) return;
      const todo = mismatches(state.targets, state.lines);
      if (!todo.length) return;

      const sections = bundledSections();
      let lines = state.lines;
      let cart = null;
      let changed = false;
      for (const [variant, want] of todo) {
        const [lastWant, n = 0] = repushed.get(variant) || [];
        const times = lastWant === want ? n + 1 : 0;
        if (times > 2) continue;
        repushed.set(variant, [want, times]);
        try {
          const keys = lines.filter((l) => l.v === variant).map((l) => l.k);
          try {
            cart = await pushGift(variant, want, keys, sections);
          } catch (error) {
            if (error.status !== 400) throw error;
            // Line keys change with quantities and discounts, so a key can be stale: re-read the cart, retry once.
            const fresh = await fetchCart();
            cart = await pushGift(variant, want, fresh.items.filter((item) => item.variant_id === variant).map((item) => item.key), sections);
          }
          lines = linesOf(cart);
          changed = true;
        } catch (error) {
          console.warn('[MFCart] gift sync failed', variant, error); // e.g. a tier gift not published: others still sync
        }
      }
      if (!changed) return;

      if (onCartPage()) {
        // Reload only on progress: a refused or capped quantity must not reload the page forever.
        if (mismatches(state.targets, lines).length < todo.length) window.location.reload();
        return;
      }
      fire('cart:change', { baseEvent: 'variant:add', cart });
      verifyAfterThemeSwap();
    } catch (error) {
      console.warn('[MFCart] gift sync failed', error);
    } finally {
      busy = false;
    }
  }

  const sectionsHtml = (cart) => {
    const sections = cart && cart.sections;
    if (!sections) return null;
    return Object.values(sections).find((h) => typeof h === 'string' && h.includes('data-mf-cart-state')) || null;
  };

  document.addEventListener('cart:change', (event) => {
    if (onCartPage()) return; // the /cart page reloads after each change and reconciles on load
    schedule(sectionsHtml(event.detail && event.detail.cart));
  });
  // Impact's full re-render paths (buy-box error, back/forward cache) carry no cart: re-read and reconcile.
  document.addEventListener('cart:refresh', () => { if (!onCartPage()) schedule(null); });
  window.addEventListener('pageshow', (event) => { if (event.persisted) schedule(onCartPage() ? DOM : null); });

  // Re-render drawer (and count, free-shipping bar) the way Impact does after an add, optionally opening it.
  async function refresh({ open = true } = {}) {
    if (onCartPage() || settings().cartType === 'page') {
      window.location.href = `${root()}cart`;
      return null;
    }
    const ids = bundledSections();
    const [cart, rendered] = await Promise.all([
      fetchCart(),
      ids.length ? fetch(`${root()}?sections=${ids.join(',')}`).then((r) => r.json()) : Promise.resolve({})
    ]);
    cart.sections = rendered;
    if (open) fire('variant:add', { items: [], cart });
    fire('cart:change', { baseEvent: 'variant:add', cart });
    return cart;
  }

  // Run a cart mutation from a control, then re-render (drawer) or reload (/cart). The control is busy meanwhile.
  async function mutate(control, task) {
    control.setAttribute('aria-busy', 'true');
    control.disabled = true;
    try {
      await task();
    } catch (error) {
      console.warn('[MFCart] cart update failed', error);
    }
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    await refresh({ open: false }).catch((error) => console.warn('[MFCart] refresh failed', error));
    control.removeAttribute('aria-busy');
    control.disabled = false;
  }

  /* ---------- controls ---------- */

  document.addEventListener('change', (event) => {
    const el = event.target;
    if (!el.matches) return;

    if (el.matches('[data-mf-protection]')) {
      const id = parseInt(el.dataset.variant, 10);
      mutate(el, () => (el.checked
        ? postJson('cart/add.js', { items: [{ id, quantity: 1 }] })
        : postJson('cart/update.js', { updates: { [id]: 0 } })));
      return;
    }

    if (el.matches('[data-mf-line-variant]')) {
      // Add the new size first (nothing is lost if it fails), then drop the old line (key, else its variant).
      const quantity = parseInt(el.dataset.quantity, 10) || 1;
      const removeOld = async () => {
        try {
          await postJson('cart/change.js', { id: el.dataset.lineKey, quantity: 0 });
        } catch (error) {
          if (error.status !== 400) throw error;
          await postJson('cart/update.js', { updates: { [el.dataset.variant]: 0 } }); // stale key: by variant
        }
      };
      mutate(el, async () => {
        await postJson('cart/add.js', { items: [{ id: parseInt(el.value, 10), quantity }] });
        try {
          await removeOld();
        } catch (error) {
          try {
            await removeOld(); // one retry, then say so: both sizes are in the cart
          } catch (retryError) {
            console.warn('[MFCart] size swap: the old size could not be removed', retryError);
          }
        }
      });
      return;
    }

    if (el.matches('.mf-upsell__select')) {
      const option = el.selectedOptions[0];
      const card = el.closest('.mf-upsell__card');
      if (!option || !card) return;
      const set = (sel, text) => {
        const node = card.querySelector(sel);
        if (!node) return;
        node.textContent = text;
        node.hidden = !text;
      };
      set('[data-mf-up-price]', option.dataset.price || '');
      set('[data-mf-up-compare]', option.dataset.compare || '');
      const pct = parseInt(option.dataset.pct, 10) || 0;
      set('[data-mf-up-pct]', pct > 0 ? `−${pct} %` : '');
    }
  });

  // −/+ (delegated, so re-rendered buttons keep working). Line keys change after every cart change (gift sync,
  // discounts) and Impact re-renders the drawer up to 1250 ms later, so a click can carry a stale key: Impact's own
  // path then gets a 400 and drops the click silently. Change the line here instead and, on 400, retry by variant.
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-mf-step]');
    if (!button) return;
    const input = button.parentElement.querySelector('input[data-line-key]');
    if (!input) return;
    const current = parseInt(input.value, 10) || 1;
    const max = parseInt(input.getAttribute('max'), 10) || Infinity;
    const target = Math.min(max, Math.max(1, current + parseInt(button.dataset.mfStep, 10)));
    if (target === current) return;
    input.value = String(target);
    mutate(button, async () => {
      try {
        await postJson('cart/change.js', { id: input.dataset.lineKey, quantity: target });
      } catch (error) {
        if (error.status !== 400) throw error;
        const line = (await fetchCart()).items.find((item) => item.variant_id === parseInt(input.dataset.variant, 10));
        if (line) await postJson('cart/change.js', { id: line.key, quantity: target });
      }
    });
  });

  // "Complétez votre cuisine": POST /cart/add.js, then refresh. Without JS the form posts to /cart/add natively.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest && event.target.closest('form[data-mf-upsell]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    button && button.setAttribute('aria-busy', 'true');
    try {
      const data = new FormData(form);
      await postJson('cart/add.js', { items: [{ id: parseInt(data.get('id'), 10), quantity: 1 }] });
      await refresh({ open: true });
    } catch (error) {
      console.warn('[MFCart] add failed', error);
    } finally {
      button && button.removeAttribute('aria-busy');
    }
  });

  window.MFCart = { refresh, reconcile: () => schedule(null) };

  // Initial pass: paint, watch the drawer, fix carts left by the previous app, and the /cart page after each reload.
  const start = () => {
    paint(document);
    watchDrawer();
    schedule(DOM);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
